import React, { useState, useCallback } from "react";
import { MessageSquare, Send, Users, ExternalLink, Sparkles, Lock, Heart, Bell, Star, Share2, Mail, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { FrontendSocialGroupType } from "@/hooks/useFrontendSocialGroups";
import { useInteractionSounds } from "@/hooks/useInteractionSounds";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import confetti from "canvas-confetti";

// WhatsApp Icon
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Telegram Icon
const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

// X Icon
const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Small Telegram Icon
const TelegramIconSmall = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

// Small WhatsApp Icon
const WhatsAppIconSmall = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

interface SocialGroupCardProps {
  group: FrontendSocialGroupType;
  isRTL: boolean;
}

export const SocialGroupCard: React.FC<SocialGroupCardProps> = ({ group, isRTL }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLoginPromptOpen, setIsLoginPromptOpen] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const { playHoverSound, playClickSound, playShareSound } = useInteractionSounds();
  const { user } = useAuth();
  const navigate = useNavigate();

  const platformName = group.type === "whatsapp" ? (isRTL ? "واتساب" : "WhatsApp") : (isRTL ? "تيليجرام" : "Telegram");
  const groupName = group.name[isRTL ? "ar" : "en"];
  const shareUrl = `https://visitevent.live/social-group/${group.id}`;
  const shareMessage = isRTL 
    ? `💬 انضم لمجموعتنا على ${platformName}!\n\n📢 ${groupName}\n👥 ${group.members.toLocaleString()} عضو\n\n🔗 سجل للانضمام:`
    : `💬 Join our ${platformName} community!\n\n📢 ${groupName}\n👥 ${group.members.toLocaleString()} members\n\n🔗 Register to join:`;
  const encodedShareMessage = encodeURIComponent(shareMessage);
  const encodedShareUrl = encodeURIComponent(shareUrl);

  const handleShareClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    playShareSound(!showShareMenu);
    setShowShareMenu(!showShareMenu);
    setCopied(false);
  }, [showShareMenu, playShareSound]);

  const handleCopyLink = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success(isRTL ? "تم نسخ الرابط" : "Link copied!");
    setTimeout(() => setCopied(false), 2000);
  }, [shareUrl, isRTL]);

  const registrationBenefits = [
    {
      icon: MessageSquare,
      title: isRTL ? "الانضمام للمجموعات" : "Join Groups",
      description: isRTL ? "انضم لمجتمعات الفعاليات" : "Join event communities"
    },
    {
      icon: Heart,
      title: isRTL ? "حفظ المفضلة" : "Save Favorites",
      description: isRTL ? "احفظ الفعاليات المفضلة" : "Save your favorite events"
    },
    {
      icon: Bell,
      title: isRTL ? "التذكيرات" : "Reminders",
      description: isRTL ? "تنبيهات قبل الفعاليات" : "Get notified before events"
    },
    {
      icon: Star,
      title: isRTL ? "مميزات حصرية" : "Exclusive Features",
      description: isRTL ? "وصول لمحتوى مميز" : "Access to premium content"
    }
  ];

  const triggerJoinConfetti = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;

    confetti({
      particleCount: 40,
      spread: 50,
      origin: { x, y },
      colors: group.type === "whatsapp" 
        ? ['#25D366', '#128C7E', '#075E54', '#DCF8C6'] 
        : ['#0088cc', '#229ED9', '#54A9EB', '#A8D8FF'],
      shapes: ['circle'],
      scalar: 0.7,
      gravity: 0.9,
      ticks: 120
    });
  }, [group.type]);

  const handleJoinClick = useCallback((e: React.MouseEvent) => {
    if (!user) {
      setIsLoginPromptOpen(true);
      return;
    }
    playClickSound();
    triggerJoinConfetti(e);
    setTimeout(() => {
      window.open(group.link, "_blank");
    }, 300);
  }, [group.link, playClickSound, triggerJoinConfetti, user]);

  const handleLoginPromptNavigate = useCallback(() => {
    setIsLoginPromptOpen(false);
    navigate("/auth");
  }, [navigate]);

  const platformColors = group.type === "whatsapp" 
    ? { primary: '#25D366', secondary: '#128C7E', light: '#DCF8C6' }
    : { primary: '#0088cc', secondary: '#229ED9', light: '#A8D8FF' };

  return (
    <>
      {/* Login Prompt Dialog */}
      <Dialog open={isLoginPromptOpen} onOpenChange={setIsLoginPromptOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 rounded-3xl" style={{
          background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
          boxShadow: '20px 20px 60px #d1d1d1, -20px -20px 60px #ffffff'
        }}>
          <div className="relative p-6 text-white text-center" style={{
            background: `linear-gradient(135deg, ${platformColors.primary}, ${platformColors.secondary})`
          }}>
            <div className="absolute inset-0 bg-black/10" />
            <motion.div 
              initial={{ scale: 0 }} 
              animate={{ scale: 1 }} 
              transition={{ type: "spring", delay: 0.1 }}
              className="relative"
            >
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-1">
                {isRTL ? "انضم إلينا!" : "Join Us!"}
              </h3>
              <p className="text-white/80 text-sm">
                {isRTL ? "سجل للانضمام للمجموعات" : "Register to join groups"}
              </p>
            </motion.div>
          </div>

          <div className="p-5 space-y-3">
            {registrationBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.1 }}
                className="flex items-center gap-4 p-3 rounded-2xl"
                style={{
                  background: 'linear-gradient(145deg, #f8f8f8, #ffffff)',
                  boxShadow: '3px 3px 8px #e8e8e8, -3px -3px 8px #ffffff'
                }}
              >
                <div 
                  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${platformColors.light}, ${platformColors.light}50)` }}
                >
                  <benefit.icon className="w-5 h-5" style={{ color: platformColors.primary }} />
                </div>
                <div className={isRTL ? "text-right" : "text-left"}>
                  <h4 className="font-semibold text-gray-700 text-sm">{benefit.title}</h4>
                  <p className="text-xs text-gray-500">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="p-5 pt-2 space-y-3">
            <Button 
              onClick={handleLoginPromptNavigate}
              className="w-full py-6 rounded-2xl text-base font-semibold text-white transition-all"
              style={{
                background: `linear-gradient(135deg, ${platformColors.primary}, ${platformColors.secondary})`,
                boxShadow: '5px 5px 15px #c0c0c0, -5px -5px 15px #ffffff'
              }}
            >
              {isRTL ? "سجل الآن مجاناً" : "Register Now - Free"}
            </Button>
            <button 
              onClick={() => setIsLoginPromptOpen(false)}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              {isRTL ? "لاحقاً" : "Maybe Later"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <motion.div
        className="relative"
        onMouseEnter={() => { setIsHovered(true); playHoverSound(); }}
        onMouseLeave={() => { setIsHovered(false); setShowShareMenu(false); }}
        whileHover={{ y: -8, scale: 1.02 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        {/* Share Button */}
        <div className="absolute top-3 left-3 z-20">
          <div className="relative">
            <motion.button
              onClick={handleShareClick}
              className="p-2 rounded-full bg-white/80 text-gray-600 hover:bg-blue-100 hover:text-blue-500 backdrop-blur-sm transition-all shadow-md"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Share2 className="w-4 h-4" />
            </motion.button>
            
            <AnimatePresence>
              {showShareMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.8, y: -10 }}
                  className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl p-2 flex gap-2 z-30"
                >
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodedShareMessage}&url=${encodedShareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <XIcon />
                  </a>
                  <a
                    href={`https://t.me/share/url?url=${encodedShareUrl}&text=${encodedShareMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-blue-500"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TelegramIconSmall />
                  </a>
                  <a
                    href={`https://wa.me/?text=${encodedShareMessage}%20${encodedShareUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-green-500"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <WhatsAppIconSmall />
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(groupName)}&body=${encodedShareMessage}%20${encodedShareUrl}`}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Mail className="w-4 h-4" />
                  </a>
                  <button
                    onClick={handleCopyLink}
                    className={`p-2 rounded-full transition-colors ${copied ? 'bg-green-100 text-green-600' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600'}`}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Neumorphic Card */}
        <div
          className="relative rounded-3xl p-6 pt-12 flex flex-col h-full min-h-[320px] overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
            boxShadow: isHovered 
              ? `12px 12px 24px #d1d1d1, -12px -12px 24px #ffffff, 0 0 30px ${platformColors.light}40`
              : '8px 8px 16px #d1d1d1, -8px -8px 16px #ffffff',
            transition: 'all 0.3s ease'
          }}
        >
          {/* Animated Background Gradient */}
          <motion.div
            className="absolute inset-0 rounded-3xl opacity-0"
            animate={{ opacity: isHovered ? 0.1 : 0 }}
            style={{
              background: `linear-gradient(135deg, ${platformColors.primary}, ${platformColors.secondary})`
            }}
          />

          {/* Sparkle Effect */}
          <motion.div
            className="absolute top-4 right-4"
            animate={{ 
              rotate: isHovered ? 360 : 0,
              scale: isHovered ? 1.2 : 1
            }}
            transition={{ duration: 0.5 }}
          >
            <Sparkles 
              size={20} 
              className="transition-colors duration-300"
              style={{ color: isHovered ? platformColors.primary : '#d1d1d1' }}
            />
          </motion.div>

          {/* Platform Icon with Neumorphic Style */}
          <div className="flex justify-center mb-5 relative z-10">
            <motion.div
              className="relative"
              animate={{ scale: isHovered ? 1.1 : 1 }}
              transition={{ duration: 0.3 }}
            >
              {group.image_url ? (
                <div
                  className="w-20 h-20 rounded-2xl overflow-hidden"
                  style={{
                    boxShadow: `6px 6px 12px #d1d1d1, -6px -6px 12px #ffffff, 0 0 20px ${platformColors.light}60`
                  }}
                >
                  <img 
                    src={group.image_url} 
                    alt={group.name.ar}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center text-white"
                  style={{
                    background: `linear-gradient(135deg, ${platformColors.primary}, ${platformColors.secondary})`,
                    boxShadow: `6px 6px 12px #d1d1d1, -6px -6px 12px #ffffff, 0 0 20px ${platformColors.light}60`
                  }}
                >
                  {group.type === "whatsapp" ? <WhatsAppIcon /> : <TelegramIcon />}
                </div>
              )}
              
              {/* Platform Badge */}
              <motion.div
                className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center text-white"
                style={{
                  background: `linear-gradient(135deg, ${platformColors.primary}, ${platformColors.secondary})`,
                  boxShadow: '2px 2px 6px #d1d1d1'
                }}
                animate={{ scale: isHovered ? [1, 1.2, 1] : 1 }}
                transition={{ duration: 0.4 }}
              >
                {group.type === "whatsapp" ? (
                  <MessageSquare size={14} />
                ) : (
                  <Send size={14} />
                )}
              </motion.div>
            </motion.div>
          </div>
          
          {/* Group Name */}
          <motion.h3 
            className="text-xl font-bold mb-2 text-center text-gray-800 relative z-10"
            animate={{ color: isHovered ? platformColors.secondary : '#1f2937' }}
            transition={{ duration: 0.3 }}
          >
            {group.name[isRTL ? "ar" : "en"]}
          </motion.h3>
          
          {/* Description */}
          <p className="text-gray-600 mb-4 text-center text-sm flex-grow line-clamp-2 relative z-10">
            {group.description[isRTL ? "ar" : "en"]}
          </p>
          
          {/* Members Count with Neumorphic Badge */}
          <div className="flex justify-center mb-5 relative z-10">
            <motion.div
              className="flex items-center gap-2 px-4 py-2 rounded-full"
              style={{
                background: 'linear-gradient(145deg, #f0f0f0, #ffffff)',
                boxShadow: 'inset 3px 3px 6px #d1d1d1, inset -3px -3px 6px #ffffff'
              }}
              animate={{ scale: isHovered ? 1.05 : 1 }}
            >
              <Users size={16} style={{ color: platformColors.primary }} />
              <span className="text-sm font-semibold text-gray-700">
                {group.members.toLocaleString()}
                <span className="text-gray-500 font-normal mr-1 ml-1">
                  {isRTL ? "عضو" : "members"}
                </span>
              </span>
            </motion.div>
          </div>
          
          {/* Join Button with Neumorphic Style */}
          {user ? (
            <motion.button
              onClick={handleJoinClick}
              className="w-full py-3 px-6 rounded-2xl font-bold text-white flex items-center justify-center gap-2 relative z-10 overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${platformColors.primary}, ${platformColors.secondary})`,
                boxShadow: isHovered 
                  ? `4px 4px 12px #d1d1d1, -4px -4px 12px #ffffff, 0 0 20px ${platformColors.primary}40`
                  : '4px 4px 12px #d1d1d1, -4px -4px 12px #ffffff'
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.span
                animate={{ x: isHovered ? [0, 3, 0] : 0 }}
                transition={{ duration: 0.5, repeat: isHovered ? Infinity : 0 }}
              >
                <ExternalLink size={18} />
              </motion.span>
              {isRTL 
                ? `انضم عبر ${group.type === "whatsapp" ? "واتساب" : "تيليجرام"}` 
                : `Join ${group.type === "whatsapp" ? "WhatsApp" : "Telegram"}`}
            </motion.button>
          ) : (
            <motion.button
              onClick={() => setIsLoginPromptOpen(true)}
              className="w-full py-3 px-6 rounded-2xl font-bold text-gray-600 flex items-center justify-center gap-2 relative z-10"
              style={{
                background: 'linear-gradient(145deg, #f5f5f5, #e0e0e0)',
                boxShadow: '4px 4px 12px #d1d1d1, -4px -4px 12px #ffffff'
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Lock size={18} />
              {isRTL ? "سجل للانضمام" : "Login to Join"}
            </motion.button>
          )}

          {/* Decorative Corner Elements */}
          <div 
            className="absolute top-0 right-0 w-24 h-24 opacity-10 rounded-bl-full"
            style={{ background: `linear-gradient(135deg, ${platformColors.primary}, transparent)` }}
          />
          <div 
            className="absolute bottom-0 left-0 w-16 h-16 opacity-10 rounded-tr-full"
            style={{ background: `linear-gradient(315deg, ${platformColors.secondary}, transparent)` }}
          />
        </div>
      </motion.div>
    </>
  );
};
